import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import DeleteConfirmation from '../../Dialogs/DeleteConfirmation';
import '../../../assets/css/teacherTimeTable.css';
import Loader from "../../Shared/Loader/Loader";
import APIConstant from "../../../utils/pathConstants";
import api from "../../../utils/apiClient";
import jsPDF from "jspdf";
import Toast from '../../Shared/Toast/Toast';
import autoTable from "jspdf-autotable";
import { useNavigate } from 'react-router-dom';
import { useCatchResponse } from '../../../utils/useCatchResponse';
import CommonConfig from '../../../utils/constant';
import moment from 'moment';

const TeacherTimeTableCard = (props) => {
    const { catchResponse } = useCatchResponse();
    const [securityUserID, setSecurityUserID] = useState("");
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const [isExpanded, setIsExpanded] = useState(false);
    const [timetableData, setTimetableData] = useState([]);
    const [isDeleteConfirmation, setIsDeleteConfirmation] = useState(false);
    const [deleteData, setDeleteData] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        const loginData = CommonConfig.loginData();
        setSecurityUserID(loginData?._id);
    }, []);

    const GetTimeTableData = async (teacherId) => {
        try {
            let data = {
                TeacherId: teacherId,
                UserId: securityUserID,
                FinancialYearId: props?.financialYearId,
            }
            Loader.show();
            const response = await api.post(APIConstant.path.GetTimeTableListByTeacher, data);
            if (response.success) {
                setTimetableData(response.data);
            } else {
                setTimetableData([]);
                Toast.error({ message: response?.message });
            }
            Loader.hide();
        } catch (error) {
            Loader.hide();
            console.error("Error fetching timetable:", error);
            catchResponse(error);
        }
    };

    const GetTimeTablePrintData = async (teacherId, startDate, endDate) => {
        try {
            let data = {
                TeacherId: teacherId,
                FinancialYearId: props?.financialYearId,
                StartDate: !CommonConfig.isEmpty(startDate) && startDate !== '-' ? moment(startDate).format("YYYY-MM-DD") : '',
                EndDate: !CommonConfig.isEmpty(endDate) && endDate !== '-' ? moment(endDate).format("YYYY-MM-DD") : '',
            }
            const response = await api.post(APIConstant.path.GetTimeTableByTeacher, data);
            if (response.success && response.data.length > 0) {
                const toDate = (time) => {
                    const match = time?.match(/(\d+):(\d+)\s(AM|PM)/);
                    if (!match) return null;
                    const [, hours, minutes, ampm] = match;
                    const hh = ampm === 'PM' && hours !== '12'
                        ? parseInt(hours, 10) + 12
                        : (ampm === 'AM' && hours === '12' ? 0 : parseInt(hours, 10));
                    const d = new Date();
                    d.setHours(hh, parseInt(minutes, 10), 0, 0);
                    return d;
                };
                const startMs = (timeRange) => {
                    const d = toDate((timeRange || '').split(' - ')[0]);
                    return d ? d.getTime() : Number.MAX_SAFE_INTEGER;
                };
                const isTeaching = (p) => p?.SubjectId && p.SubjectId !== 'Free' && p.SubjectId !== 'Break';

                const slotSet = new Set();
                (response.data || []).forEach(day => (day?.Periods || []).forEach(p => {
                    if (isTeaching(p) && p?.Time) slotSet.add(p.Time);
                }));
                let slotTimes = [...slotSet].sort((a, b) => startMs(a) - startMs(b));
                if (!slotTimes.length) {
                    const fallback = response.data?.find(d => (d?.Periods || []).length)?.Periods?.[0]?.Time;
                    if (fallback) slotTimes = [fallback];
                }

                const periodData = slotTimes.map((time) => ({ Time: time, isBreak: false }));

                const timeTableData = (response.data || []).map(row => {
                    const formattedRow = { Day: row?.Day, id: row?.id };
                    const byTime = new Map();
                    (row?.Periods || []).forEach(p => { if (p?.Time) byTime.set(p.Time, p); });
                    slotTimes.forEach((time, index) => {
                        const period = byTime.get(time);
                        const teaching = period && isTeaching(period);
                        formattedRow[`Period${index + 1}`] = teaching ? period.SubjectName : 'Free';
                        formattedRow[`Class${index + 1}`] = teaching ? period.ClassName : 'Free';
                    });
                    return formattedRow;
                });
                const sortedData = timeTableData.sort((a, b) => {
                    return days.indexOf(a?.Day) - days.indexOf(b?.Day);
                });

                printTimeTable(sortedData, periodData);
            } else {
                Toast.error({ message: response?.message });
            }
        } catch (error) {
            console.error("Error fetching timetable:", error);
            catchResponse(error);
        }
    };

    const toggleExpand = (value) => {
        if (value) {
            GetTimeTableData(props?.TeacherId);
        }
        setIsExpanded(value);
    };

    const printTimeTable = (timetablePrintData, periodsData) => {
        try {
            if (timetablePrintData.length === 0) {
                Toast.error({ message: "No data available for printing." });
                return;
            }
            Loader.show();
            const doc = new jsPDF();
            doc.setFontSize(16);
            doc.text(`Time Table for Teacher: ${props?.TeacherName}`, 10, 10);

            const columns = [
                { header: "Days/Periods", dataKey: "Day" },
                ...periodsData.map((period, index) => ({
                    header: period?.Time,
                    dataKey: `Period${index + 1}`,
                })),
            ];

            const rows = timetablePrintData?.map((row) => {
                const formattedRow = { Day: row.Day };
                periodsData.forEach((_, index) => {
                    formattedRow[`Period${index + 1}`] = row[`Period${index + 1}`] === 'Free' ? row[`Class${index + 1}`] : `${row[`Period${index + 1}`] ?? 'Free'} - ${row[`Class${index + 1}`] ?? 'Free'}`;
                });
                return formattedRow;
            });

            autoTable(doc, {
                columns: columns,
                body: rows,
                startY: 20,
                theme: "grid",
            });
            Loader.hide();
            doc.save(`Teacher_Time_Table_${props?.TeacherName || "Unknown"}.pdf`);
        } catch (error) {
            console.log('Error ');
            Loader.hide();
        }
    };

    const actionBodyTemplateSrNo = (data, props) => {
        return props.rowIndex + 1;
    };

    const actionBodyTemplate = (rows) => {
        return (
            <React.Fragment>
                <div className='p-d-flex p-ai-center'>
                    <Button icon="pi pi-pencil" className="e-sction-btn" onClick={() => navigate('/edit-teacher-time-table', { state: { classList: props?.ClassList, TeacherId: props?.TeacherId, financialYearId: props?.financialYearId, academicYearList: props?.academicYearList, startDate: rows?.StartDateRaw, endDate: rows?.EndDateRaw } })} />
                    <Button icon="pi pi-print" className="e-sction-btn p-mx-2" onClick={async () => {
                        await GetTimeTablePrintData(props?.TeacherId, rows?.StartDateRaw, rows?.EndDateRaw);

                    }} />
                    <Button icon="pi pi-trash" className="e-sction-btn" onClick={() => {
                        setIsDeleteConfirmation(true);
                        setDeleteData({ StartDateRaw: rows?.StartDateRaw, EndDateRaw: rows?.EndDateRaw });
                    }} />
                </div>
            </React.Fragment>

        );
    };

    const handleClose = () => {
        setIsDeleteConfirmation(false);
    };

    const renderFooter = () => (
        <div className="dialog-footer p-d-flex p-ai-center p-jc-end">
            <Button label="Cancel" className="btn-text-action-secondary" onClick={handleClose} />
            <Button label="Delete" className="btn-text-action-primary" onClick={() => DeleteTimeTable()} />
        </div>
    );

    const DeleteTimeTable = async () => {
        try {
            let data = {
                TeacherId: props?.TeacherId,
                FinancialYearId: props?.financialYearId,
                UserId: securityUserID,
                StartDate: !CommonConfig.isEmpty(deleteData?.StartDateRaw) ? moment(deleteData?.StartDateRaw).format("YYYY-MM-DD") : '',
                EndDate: !CommonConfig.isEmpty(deleteData?.EndDateRaw) ? moment(deleteData?.EndDateRaw).format("YYYY-MM-DD") : '',
            };
            Loader.show();
            await api.post(APIConstant.path.DeleteTeacherTimeTable, data).then(async response => {
                Loader.hide();
                if (response.success) {
                    Toast.success({ message: 'Time table deleted successfully' });
                    setDeleteData({});
                    GetTimeTableData(props?.TeacherId);
                } else {
                    Toast.error({ message: response?.message });
                }
                handleClose();
            }).catch(err => {
                handleClose();
                Loader.hide();
                console.log(err);
                catchResponse(err);
            });
        } catch (err) {
            Loader.hide();
            console.log(err);
            catchResponse(err);
        }
    };

    return (
        <div className='time-table'>
            <div className="">
                <div className="p-d-flex p-ai-center p-jc-between">
                    <h2 className='p-mb-0'>{`Teacher : ${props?.TeacherName}`}</h2>
                    <div className="p-d-flex p-ai-center p-jc-between">
                        <Button
                            icon={`pi ${isExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`}
                            className="e-sction-btn"
                            onClick={() => toggleExpand(!isExpanded)}
                        />
                    </div>
                </div>
                {(isExpanded) && (
                    <div className="p-mt-3">
                        <DataTable value={timetableData} responsiveLayout="scroll" className="custom-table" scrollHeight='min-content'>
                            <Column field="id" body={actionBodyTemplateSrNo} header="Sr No." />
                            <Column field="StartDate" header="Start Date" />
                            <Column field="EndDate" header="End Date" />
                            <Column body={actionBodyTemplate} header="Action" headerClassName='e-template-action' bodyClassName='e-template-action' headerStyle={{ display: 'flex', justifyContent: 'end' }} />
                        </DataTable>
                    </div>
                )}

            </div>
            <Dialog
                visible={isDeleteConfirmation}
                onHide={handleClose}
                header={
                    <div className="p-d-flex p-jc-between p-ai-center">
                        <span>Delete Confirmation</span>
                    </div>
                }
                footer={renderFooter()}
                draggable={false}
                dismissableMask
                position="center"
                style={{ width: '450px' }}
            >
                <DeleteConfirmation />
            </Dialog>
        </div>

    );
};

export default TeacherTimeTableCard;
